import {debounce, lowerCase, toPairs, isString, isArray, sortBy} from 'lodash';
import Button from '@material-ui/core/Button';
import PopupCloseButton from '../PopupCloseButton';
import WaypointStats from '../../WaypointStats';
import React, {useState, useRef, useCallback} from 'react';
import ReactDOM from 'react-dom';
import trackAnalytics from '../../../../utils/gaia/trackAnalytics';
import TextField from '@material-ui/core/TextField';
import theme from '../../../../theme';
import OutlinedSelect, {OutlinedSelectProps} from '../../OutlinedSelect';
import waypointIcons from '../../../../icons/waypoints/icons';
import waypointPins from '../../../../icons/waypoints/pins';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {WaypointPopupProps} from './index';
import {getPointRemoteId} from '../../../../utils/gaia/savePoint';
import useGetSyncPromise from '../../../../utils/react/useGetSyncPromise';

const sortedIcons = [
    ...sortBy(toPairs(waypointPins), ([key]) => key),
    ...sortBy(toPairs(waypointIcons), ([key]) => key),
];

const debouncedEditWaypointAttributesEvent = debounce(edited => {
    trackAnalytics('Edit Waypoint Attributes', {edited, category: 'map2'});
}, 1000, {leading: false, trailing: true});

export default function WaypointPopup(props: WaypointPopupProps) {
    const {
        waypoint,
        waypointDispatcher,
        onWaypointMoveStart,
        onTrackMoveStart,
        onPopupChange,
        interaction,
        mapboxLayerId,
    } = props;
    const {notes = '', title = '', icon} = waypoint.properties;

    const getSyncPromise = useGetSyncPromise();
    const remoteId = getSyncPromise(getPointRemoteId(waypoint))[0];
    const pathname = remoteId && `/datasummary/waypoint/${remoteId}/`;

    const [moreOpen, setMoreOpen] = useState<boolean>(false);
    const moreRef = useRef();

    let photos = [];
    if (waypoint.properties.photos && waypoint.properties.photos.length > 0) {
        if (isString(waypoint.properties.photos)) {
            photos = JSON.parse(waypoint.properties.photos);
        } else if (isArray(waypoint.properties.photos)) {
            photos = waypoint.properties.photos;
        }
    }

    return <div style={{width: 250, display: 'flex', flexDirection: 'column'}}>
        <PopupCloseButton onClick={useCallback(() => onPopupChange(undefined), [onPopupChange])} />

        <div style={{display: 'flex', flexDirection: 'row'}}>
            <TextField
                value={title}
                label="Title"
                margin='dense'
                variant='outlined'
                disabled={!waypoint.properties.isTitleLoaded}
                onChange={(event: any) => {
                    debouncedEditWaypointAttributesEvent('Title');
                    waypointDispatcher({
                        type: 'updateProperties',
                        properties: {...waypoint.properties, title: event.target.value},
                        save: true
                    });
                }}
                style={{flex: 1, marginRight: theme.spacing(1)}}
                inputProps={{maxLength: 500}}

            />
            <IconSelect
                onChange={(event: any) => {
                    debouncedEditWaypointAttributesEvent('Icon');
                    waypointDispatcher({
                        type: 'updateProperties',
                        properties: {...waypoint.properties, icon: event.target.value},
                        save: true,
                    });
                }}
                value={icon || 'red-pin-down.png'}
            />
        </div>

        <TextField
            label="Notes"
            margin='dense'
            multiline
            onChange={(event: any) => {
                debouncedEditWaypointAttributesEvent('Notes');
                waypointDispatcher({
                    type: 'updateProperties',
                    properties: {...waypoint.properties, notes: event.target.value},
                    save: true,
                });
            }}
            rows={2}
            rowsMax={10}
            value={notes}
            variant='outlined'
        />

        {photos.length > 0 && window.location.pathname !== pathname &&
            <div style={{display: 'flex', flexDirection: 'row', marginTop: theme.spacing(1)}}>
                {photos.map((photo: Photo, idx: number) =>
                    <PhotoThumbnail photo={photo} key={idx} />
                )}
            </div>
        }

        <div style={{
            display: 'flex',
            flexDirection: 'row',
            marginTop: theme.spacing(1),
            alignItems: 'flex-end',
        }}>
            <WaypointStats interaction={interaction} waypoint={waypoint} />

            <div>
                <Button
                    onClick={() => setMoreOpen(!moreOpen)}
                    variant="contained"
                    color='primary'
                    buttonRef={moreRef}
                    style={{fontSize: 12, color: '#fff'}}
                >
                        More
                    <ExpandMoreIcon style={{width: 16, marginRight: -4, marginLeft: 2}} />
                </Button>
            </div>

            <Menu
                onClose={() => setMoreOpen(false)}
                open={moreOpen}
                anchorEl={moreRef.current}
            >
                {window.location.pathname !== pathname && <MenuItem
                    style={{fontSize: 12}}
                    href={pathname}
                    component='a'
                    onClick={() => trackAnalytics('View Waypoint Details', {category: 'map2'})}
                >
                        Open Details Page
                </MenuItem>}

                {!waypoint.properties.trackLocalId && <MenuItem
                    style={{fontSize: 12}}
                    onClick={() => {
                        trackAnalytics('Move Waypoint', {category: 'map2'});
                        onWaypointMoveStart(waypoint, mapboxLayerId);
                        setMoreOpen(false);
                    }}
                >
                        Move
                </MenuItem>}

                {waypoint.properties.trackLocalId && <MenuItem
                    style={{fontSize: 12}}
                    onClick={() => onTrackMoveStart(
                        waypoint.properties.trackLocalId!,
                        mapboxLayerId
                    )}
                >
                    Edit Route
                </MenuItem>}

                {window.location.pathname !== pathname && <MenuItem
                    style={{fontSize: 12}}
                    onClick={() => {
                        trackAnalytics('Delete Waypoint', {category: 'map2'});
                        setMoreOpen(false);
                        onPopupChange(undefined);
                        if (waypoint.properties.trackLocalId) {
                            waypointDispatcher({
                                save: true,
                                type: 'updateProperties',
                                properties: {
                                    ...waypoint.properties,
                                    attr: 'route'
                                }
                            });
                        } else {
                            waypointDispatcher({type: 'remove', waypoint});
                        }
                    }}
                >
                    Delete
                </MenuItem>}
            </Menu>
        </div>
    </div>;
}

const iconWidth = 24;

function IconSelect(props: Partial<OutlinedSelectProps>) {

    return <OutlinedSelect
        style={{marginTop: theme.spacing(1)}}
        label="Icon"
        renderValue={value =>
            <img
                width={iconWidth}
                style={{position: 'relative', top: 2}}
                src={(
                    waypointIcons[value as keyof typeof waypointIcons] ||
                    waypointPins[value as keyof typeof waypointPins] ||
                    waypointPins['red-pin-down.png']
                )}
            />
        }
        SelectDisplayProps={{
            style: {
                borderRadius: theme.shape.borderRadius,
                paddingBottom: 6,
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(2.5),
                paddingTop: 6,
            },
        }}
        MenuProps={{
            transitionDuration: 100,
            MenuListProps: {
                style: {
                    display: 'flex',
                    flexWrap: 'wrap',
                    width: (iconWidth + theme.spacing(2)) * 12,
                    flexGrow: 1,
                }
            }
        }}
        {...props}
    >
        {sortedIcons.map(([key, src]: [string, string]) => (
            <MenuItem
                style={{paddingLeft: theme.spacing(1), paddingRight: theme.spacing(1)}}
                key={key}
                value={key}
                title={formatIconTitle(key)}
            >
                <img width={iconWidth} src={src} />
            </MenuItem>
        ))}
    </OutlinedSelect>;
}

function formatIconTitle(key: string) {
    return lowerCase(key
        .replace('pin-down', 'pin')
        .replace('-24', '')
        .replace('.png', '')
    );
}

function PhotoThumbnail({photo}: {photo: Photo}) {
    return <a
        href={photo.fullsize_url}
        target='_blank'
        rel='noopener noreferrer'
        style={{marginRight: theme.spacing(1)}}
    >
        <div style={{
            background: `url(${photo.thumbnail_url}) 50% 50% no-repeat`,
            width: '50px',
            height: '50px',
        }} />
    </a>;
}
